<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useUserStore } from "@/store/user-store";
import AnButton from "@/components/common/AnButton.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Select from "primevue/select";
import Textarea from "primevue/textarea";
import supportCategories from "@/lib/support-categories.json";

const store = useUserStore();

// State
const tickets = ref([]);
const isLoadingTickets = ref(false);
const isSubmitting = ref(false);
const selectedCategory = ref(null);
const selectedSubCategory = ref(null);
const notes = ref("");
const characterCount = ref(0);
const successMessage = ref("");
const errorMessage = ref("");

// Computed
const categories = computed(() => {
  return supportCategories.categories.map(cat => ({
    value: cat.id,
    label: cat.label
  }));
});

const subCategories = computed(() => {
  if (!selectedCategory.value) return [];
  const category = supportCategories.categories.find(c => c.id === selectedCategory.value);
  if (!category) return [];
  return category.subCategories.map(sub => ({
    value: sub.id,
    label: sub.label
  }));
});

const characterRange = computed(() => {
  const remaining = 200 - characterCount.value;
  return `${characterCount.value}/200 characters (${remaining} remaining)`;
});

const isFormValid = computed(() => {
  return selectedCategory.value &&
         selectedSubCategory.value &&
         notes.value.trim().length >= 10 &&
         notes.value.trim().length <= 200;
});

// Methods
const updateCharacterCount = () => {
  characterCount.value = notes.value.length;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  // Handle different date formats
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  } catch (error) {
    return dateStr;
  }
};

const getStatusClass = (status) => {
  return status?.toLowerCase() === 'open' ? 'status-open' : 'status-closed';
};

const resetForm = () => {
  selectedCategory.value = null;
  selectedSubCategory.value = null;
  notes.value = "";
  characterCount.value = 0;
  errorMessage.value = "";
  successMessage.value = "";
};

const loadTickets = async () => {
  try {
    isLoadingTickets.value = true;
    errorMessage.value = "";
    const response = await store.getCustomerSupportTickets();

    if (response?.success === "Success" && response?.tickets) {
      tickets.value = response.tickets.sort((a, b) =>
        new Date(b.Date) - new Date(a.Date)
      );
    } else {
      tickets.value = [];
    }
  } catch (error) {
    console.error("Error loading tickets:", error);
    errorMessage.value = "Failed to load support tickets";
    tickets.value = [];
  } finally {
    isLoadingTickets.value = false;
  }
};

const submitTicket = async () => {
  if (!isFormValid.value) {
    store.fireAlert("Error", "Please fill all fields correctly");
    return;
  }

  try {
    isSubmitting.value = true;
    errorMessage.value = "";

    const categoryLabel = categories.value.find(c => c.value === selectedCategory.value)?.label;
    const subCategoryLabel = subCategories.value.find(s => s.value === selectedSubCategory.value)?.label;

    const payload = {
      Category: categoryLabel,
      'Sub-Category': subCategoryLabel,
      Notes: notes.value.trim()
    };

    const response = await store.logCustomerSupportTicket(payload);

    if (response?.success === "Success") {
      successMessage.value = "Support ticket created successfully!";
      store.fireAlert("Success", "Your ticket has been submitted. Our team will review it shortly.");
      resetForm();
      await loadTickets();

      setTimeout(() => {
        successMessage.value = "";
      }, 5000);
    }
  } catch (error) {
    console.error("Error submitting ticket:", error);
    errorMessage.value = "Failed to submit ticket. Please try again.";
    store.fireAlert("Error", "Failed to submit support ticket");
  } finally {
    isSubmitting.value = false;
  }
};

// Watch for category changes to reset subcategory
watch(selectedCategory, () => {
  selectedSubCategory.value = null;
});

// Lifecycle
onMounted(() => {
  loadTickets();
});
</script>

<template>
  <div class="operations-support-container">
    <div v-if="successMessage" class="message-banner success-banner">
      <i class="pi pi-check-circle"></i>
      <span>{{ successMessage }}</span>
    </div>
    <div v-if="errorMessage" class="message-banner error-banner">
      <i class="pi pi-exclamation-circle"></i>
      <span>{{ errorMessage }}</span>
    </div>

    <!-- My Tickets Section -->
    <div class="tickets-section section-card">
      <div class="section-header">
        <h2 class="section-title">My Support Tickets</h2>
        <span class="ticket-count">{{ tickets.length }} ticket(s)</span>
      </div>

      <div v-if="isLoadingTickets" class="loading-state">
        <i class="pi pi-spin pi-spinner"></i>
        <p>Loading tickets...</p>
      </div>

      <div v-else-if="tickets.length === 0" class="empty-state">
        <i class="pi pi-inbox"></i>
        <p>No support tickets found</p>
        <small>Create your first ticket below</small>
      </div>

      <div v-else class="tickets-table-container">
        <DataTable
          :value="tickets"
          :paginator="true"
          :rows="5"
          :lazy="false"
          class="support-tickets-table"
          responsiveLayout="scroll"
        >
          <Column field="Date" header="Date" :sortable="true" style="min-width: 120px">
            <template #body="slotProps">
              {{ formatDate(slotProps.data.Date) }}
            </template>
          </Column>
          <Column field="Category" header="Category" :sortable="true" style="min-width: 150px">
            <template #body="slotProps">
              {{ slotProps.data.Category }}
            </template>
          </Column>
          <Column field="Sub-Category" header="Issue Type" :sortable="true" style="min-width: 180px">
            <template #body="slotProps">
              {{ slotProps.data['Sub-Category'] }}
            </template>
          </Column>
          <Column field="Notes" header="Description" style="min-width: 250px">
            <template #body="slotProps">
              <div class="notes-cell">{{ slotProps.data.Notes }}</div>
            </template>
          </Column>
          <Column field="Status" header="Status" :sortable="true" style="min-width: 100px">
            <template #body="slotProps">
              <span :class="['status-badge', getStatusClass(slotProps.data.Status)]">
                {{ slotProps.data.Status }}
              </span>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>

    <!-- Raise New Ticket Section -->
    <div class="raise-ticket-section section-card">
      <div class="section-header">
        <h2 class="section-title">Raise a New Ticket</h2>
        <p class="section-description">Please provide details about the issue you're facing</p>
      </div>

      <div class="ticket-form">
        <div class="form-row">
          <div class="form-group">
            <label for="category" class="form-label">
              Category <span class="required">*</span>
            </label>
            <Select
              v-model="selectedCategory"
              :options="categories"
              optionLabel="label"
              optionValue="value"
              placeholder="Select a category"
              class="form-select"
              inputId="category"
            />
          </div>

          <div class="form-group">
            <label for="subcategory" class="form-label">
              Issue Type <span class="required">*</span>
            </label>
            <Select
              v-model="selectedSubCategory"
              :options="subCategories"
              optionLabel="label"
              optionValue="value"
              placeholder="Select issue type"
              class="form-select"
              inputId="subcategory"
              :disabled="!selectedCategory"
            />
          </div>
        </div>

        <div class="form-group">
          <div class="label-with-counter">
            <label for="notes" class="form-label">
              Describe Your Issue <span class="required">*</span>
            </label>
            <span class="character-counter" :class="{ 'invalid': characterCount < 10 || characterCount > 200 }">
              {{ characterRange }}
            </span>
          </div>
          <Textarea
            v-model="notes"
            @input="updateCharacterCount"
            placeholder="Please describe the issue you're facing (10-200 characters)"
            :rows="5"
            class="form-textarea"
            inputId="notes"
            :maxlength="200"
          />
          <small v-if="characterCount > 0 && characterCount < 10" class="validation-hint error">
            Minimum 10 characters required
          </small>
        </div>

        <div class="form-actions">
          <AnButton
            label="Submit Ticket"
            @click="submitTicket"
            :disabled="!isFormValid || isSubmitting"
            :loading="isSubmitting"
            class="submit-button"
          />
          <AnButton
            label="Clear Form"
            @click="resetForm"
            :disabled="isSubmitting"
            class="clear-button"
            severity="secondary"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.operations-support-container {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 20px;
}

/* Page Header */
.page-header {
  margin-bottom: 30px;
  text-align: center;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 1rem;
  color: var(--an-text-muted);
  margin: 0;
}

/* Message Banners */
.message-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-weight: 500;
}

.success-banner {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.error-banner {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.message-banner i {
  font-size: 1.2rem;
}

/* Section Cards */
.section-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 24px;
}

.section-header {
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--an-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0;
}

.section-description {
  font-size: 0.9rem;
  color: var(--an-text-muted);
  margin: 4px 0 0 0;
}

.ticket-count {
  background: var(--an-primary);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--an-text-muted);
}

.loading-state i,
.empty-state i {
  font-size: 3rem;
  margin-bottom: 16px;
  display: block;
}

.empty-state p {
  font-size: 1.1rem;
  margin: 0 0 8px 0;
  color: var(--an-text-primary);
}

.empty-state small {
  font-size: 0.9rem;
}

/* Tickets Table */
.tickets-table-container {
  margin-top: 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.support-tickets-table {
  font-size: 0.9rem;
  width: 100%;
  min-width: 800px;
}

.notes-cell {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-word;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-open {
  background: #fff3cd;
  color: #856404;
}

.status-closed {
  background: #d4edda;
  color: #155724;
}

/* Form Styling */
.ticket-form {
  margin-top: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-weight: 600;
  color: var(--an-text-primary);
  font-size: 0.95rem;
}

.required {
  color: #dc3545;
}

.form-select,
.form-textarea {
  width: 100%;
}

.label-with-counter {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.character-counter {
  font-size: 0.85rem;
  color: var(--an-text-muted);
  font-weight: 500;
}

.character-counter.invalid {
  color: #dc3545;
}

.validation-hint {
  font-size: 0.85rem;
  margin-top: 4px;
}

.validation-hint.error {
  color: #dc3545;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.submit-button {
  background: var(--an-primary);
  color: white;
  padding: 10px 24px;
  border-radius: 6px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-button:hover:not(:disabled) {
  background: var(--an-primary-dark);
  transform: translateY(-2px);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.clear-button {
  padding: 10px 24px;
  border-radius: 6px;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .operations-support-container {
    padding: 16px;
  }

  .section-card {
    padding: 18px;
  }

  .support-tickets-table {
    font-size: 0.85rem;
    min-width: 750px;
  }
}

@media (max-width: 1024px) {
  .operations-support-container {
    padding: 14px;
  }

  .section-card {
    padding: 16px;
  }

  .support-tickets-table {
    font-size: 0.85rem;
    min-width: 700px;
  }

  .form-row {
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .operations-support-container {
    padding: 12px;
  }

  .section-card {
    padding: 14px;
    margin-bottom: 20px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .section-description {
    font-size: 0.85rem;
  }

  .ticket-count {
    font-size: 0.8rem;
    padding: 3px 10px;
  }

  /* Table Responsive */
  .tickets-table-container {
    margin-left: -14px;
    margin-right: -14px;
    width: calc(100% + 28px);
  }

  .support-tickets-table {
    font-size: 0.8rem;
    min-width: 650px;
  }

  .support-tickets-table :deep(th),
  .support-tickets-table :deep(td) {
    padding: 10px 8px;
  }

  .notes-cell {
    max-width: 180px;
    font-size: 0.8rem;
  }

  .status-badge {
    padding: 3px 8px;
    font-size: 0.75rem;
  }

  /* Form Responsive */
  .form-row {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 14px;
  }

  .form-group {
    gap: 6px;
  }

  .form-label {
    font-size: 0.9rem;
  }

  .character-counter {
    font-size: 0.8rem;
  }

  .form-actions {
    flex-direction: column;
    gap: 10px;
  }

  .submit-button,
  .clear-button {
    width: 100%;
    padding: 12px 20px;
    justify-content: center;
  }

  /* Message Banners */
  .message-banner {
    padding: 10px 14px;
    font-size: 0.9rem;
    margin-bottom: 16px;
  }

  /* Empty & Loading States */
  .loading-state,
  .empty-state {
    padding: 40px 16px;
  }

  .loading-state i,
  .empty-state i {
    font-size: 2.5rem;
  }

  .empty-state p {
    font-size: 1rem;
  }
}

@media (max-width: 640px) {
  .operations-support-container {
    padding: 10px;
  }

  .section-card {
    padding: 12px;
    margin-bottom: 16px;
    border-radius: 8px;
  }

  .section-header {
    margin-bottom: 14px;
    padding-bottom: 10px;
  }

  .section-title {
    font-size: 1.15rem;
  }

  .section-description {
    font-size: 0.8rem;
  }

  .ticket-count {
    font-size: 0.75rem;
    padding: 3px 8px;
  }

  /* Table for smaller screens */
  .tickets-table-container {
    margin-left: -12px;
    margin-right: -12px;
    width: calc(100% + 24px);
    overflow-x: auto;
  }

  .support-tickets-table {
    font-size: 0.75rem;
    min-width: 600px;
  }

  .support-tickets-table :deep(th),
  .support-tickets-table :deep(td) {
    padding: 8px 6px;
    white-space: nowrap;
  }

  .notes-cell {
    max-width: 150px;
    font-size: 0.75rem;
  }

  .status-badge {
    padding: 2px 6px;
    font-size: 0.7rem;
  }

  /* Form adjustments */
  .form-group {
    gap: 6px;
  }

  .form-label {
    font-size: 0.85rem;
  }

  .character-counter {
    font-size: 0.75rem;
  }

  .label-with-counter {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .form-textarea {
    font-size: 0.9rem;
    min-height: 100px;
  }

  .validation-hint {
    font-size: 0.75rem;
  }

  .form-actions {
    gap: 8px;
    margin-top: 16px;
  }

  .submit-button,
  .clear-button {
    padding: 11px 18px;
    font-size: 0.9rem;
  }

  /* Message Banners */
  .message-banner {
    padding: 10px 12px;
    font-size: 0.85rem;
    margin-bottom: 12px;
  }

  .message-banner i {
    font-size: 1rem;
  }

  /* Empty & Loading States */
  .loading-state,
  .empty-state {
    padding: 30px 12px;
  }

  .loading-state i,
  .empty-state i {
    font-size: 2rem;
    margin-bottom: 10px;
  }

  .empty-state p {
    font-size: 0.95rem;
  }

  .empty-state small {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .operations-support-container {
    padding: 8px;
  }

  .section-card {
    padding: 10px;
    margin-bottom: 12px;
  }

  .section-header {
    margin-bottom: 12px;
    padding-bottom: 8px;
  }

  .section-title {
    font-size: 1.05rem;
  }

  .section-description {
    font-size: 0.75rem;
  }

  .ticket-count {
    font-size: 0.7rem;
    padding: 2px 6px;
  }

  /* Compact table */
  .tickets-table-container {
    margin-left: -10px;
    margin-right: -10px;
    width: calc(100% + 20px);
  }

  .support-tickets-table {
    font-size: 0.7rem;
    min-width: 550px;
  }

  .support-tickets-table :deep(th),
  .support-tickets-table :deep(td) {
    padding: 6px 4px;
  }

  .notes-cell {
    max-width: 120px;
    font-size: 0.7rem;
  }

  .status-badge {
    padding: 2px 5px;
    font-size: 0.65rem;
  }

  /* Compact form */
  .form-group {
    gap: 5px;
  }

  .form-label {
    font-size: 0.8rem;
  }

  .character-counter {
    font-size: 0.7rem;
  }

  .form-select :deep(.p-select) {
    font-size: 0.85rem;
  }

  .form-textarea {
    font-size: 0.85rem;
    min-height: 90px;
  }

  .validation-hint {
    font-size: 0.7rem;
  }

  .form-actions {
    gap: 8px;
    margin-top: 14px;
  }

  .submit-button,
  .clear-button {
    padding: 10px 16px;
    font-size: 0.85rem;
  }

  /* Message Banners */
  .message-banner {
    padding: 8px 10px;
    font-size: 0.8rem;
    gap: 8px;
  }

  .message-banner i {
    font-size: 0.9rem;
  }

  /* Empty & Loading States */
  .loading-state,
  .empty-state {
    padding: 24px 10px;
  }

  .loading-state i,
  .empty-state i {
    font-size: 1.75rem;
    margin-bottom: 8px;
  }

  .empty-state p {
    font-size: 0.9rem;
  }

  .empty-state small {
    font-size: 0.75rem;
  }
}
</style>
